import {FC, useState} from 'react'
import {api, ApiError, Booking} from '../../services/api'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'
import {confirmDialog} from '../../components/dialog'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

/** Human label for a saved card, e.g. "Visa •••• 4242". */
export const cardLabelOf = (
  b: {cardBrand: string | null; cardLast4: string | null} | null | undefined,
): string | null =>
  b?.cardLast4 ? `${(b.cardBrand ?? 'card').replace(/^\w/, (c) => c.toUpperCase())} •••• ${b.cardLast4}` : null

/**
 * The single card-on-file: save it once, then deposit/rent/bed wash charge it.
 * Shown on both the renter dashboard and the Payments page.
 */
export const PaymentCardSection: FC<{booking: Booking}> = ({booking}) => {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const label = cardLabelOf(booking)
  // Saving and updating both use the setup checkout: it saves the new card, sets
  // it as default, and returns to /payments automatically (no portal to escape).
  const go = async () => {
    setBusy(true)
    try {
      const {url} = await api.saveCardSession(booking.id)
      window.location.href = url
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not open the card form')
      setBusy(false)
    }
  }
  const remove = async () => {
    const ok = await confirmDialog({
      title: 'Remove your saved card?',
      text: booking.rentActive
        ? "We'll delete it. Your monthly rent won't be able to charge until you add a new card."
        : "We'll delete it. You'll need to save a card again to pay by card.",
      confirmText: 'Remove card',
      danger: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await api.removeCard(booking.id)
      toast.success('Card removed.')
      window.location.reload()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not remove the card')
      setBusy(false)
    }
  }
  return (
    <div className='ccp-fade-up-2' style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 16, padding: '20px 24px', marginBottom: 24}}>
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
          <span style={{fontSize: 26}}>💳</span>
          <div>
            <div style={{fontSize: 15, fontWeight: 800, color: INK}}>{booking.cardOnFile ? (label ?? 'Card on file') : 'No card on file'}</div>
            <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>
              {booking.cardOnFile ? 'Used for your deposit, monthly rent and bed washes.' : 'Save a card once — then deposit, rent and bed washes charge it with no re-entering.'}
            </div>
          </div>
        </div>
        <div style={{display: 'flex', gap: 10, flexWrap: 'wrap'}}>
          <button onClick={go} disabled={busy} style={{fontWeight: 700, fontSize: 14, color: booking.cardOnFile ? ACCENT : '#fff', background: booking.cardOnFile ? '#fff' : ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 10, padding: '11px 20px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1}}>
            {busy ? <Spinner size={13} /> : booking.cardOnFile ? 'Update card' : 'Save card'}
          </button>
          {booking.cardOnFile && (
            <button onClick={remove} disabled={busy} style={{fontWeight: 700, fontSize: 14, color: '#d9214e', background: '#fff', border: '1px solid #f3c6d1', borderRadius: 10, padding: '11px 20px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1}}>
              Remove card
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
